import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { ScrollText } from 'lucide-react';

interface PMSLog {
  id: string;
  hotel_name: string;
  endpoint: string;
  method: string;
  response_status: number;
  created_at: string;
}

export function PMSLogs() {
  const [logs, setLogs] = useState<PMSLog[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('User not authenticated');
        return;
      }

      const { data, error: queryError } = await supabase
        .from('pms_log_details')
        .select('*')
        .eq('hotel_id', user.id)
        .order('created_at', { ascending: false })
        .limit(100);

      if (queryError) throw queryError;

      setLogs(data || []);
    } catch (err) {
      setError('Failed to load PMS logs');
      console.error('Error loading logs:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="px-6 py-4 text-center text-sm text-gray-500">Loading PMS logs...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center">
          <ScrollText className="h-6 w-6 text-indigo-600" />
          <h2 className="text-xl font-bold text-gray-900">PMS Logs</h2>
        </div>
      </div>

      <div className="extranet-container-body">
        {error && (
          <div className="mb-4 p-4 bg-red-50">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">Timestamp</th>
                <th className="table-header">Hotel</th>
                <th className="table-header">Method</th>
                <th className="table-header">Endpoint</th>
                <th className="table-header">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {logs.map((log) => (
                <tr key={log.id} className="table-row">
                  <td className="table-cell">
                    {new Date(log.created_at).toLocaleString()}
                  </td>
                  <td className="table-cell">
                    {log.hotel_name}
                  </td>
                  <td className="table-cell">
                    <span className={`status-badge ${
                      log.method === 'GET'
                        ? 'bg-blue-100'
                        : log.method === 'POST'
                        ? 'bg-green-100'
                        : log.method === 'PUT'
                        ? 'bg-yellow-100'
                        : 'bg-red-100'
                    }`}>
                      {log.method}
                    </span>
                  </td>
                  <td className="table-cell">
                    {log.endpoint}
                  </td>
                  <td className="table-cell">
                    <span className={`status-badge ${
                      log.response_status >= 200 && log.response_status < 300
                        ? 'status-active'
                        : 'status-error'
                    }`}>
                      {log.response_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {logs.length === 0 && !error && (
            <div className="text-center py-8">
              <p className="px-6 py-4 text-center text-sm text-gray-500">
                No logs found. Logs will appear here when SaaS providers make API calls to your PMS.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}